import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../logo.png';
import { SITE_COPY, SiteLang } from './siteConfig';
import useReveal from './useReveal';

// Scrolls to the home page's download section when present, otherwise to the footer store badges.
export const scrollToDownload = (e?: React.MouseEvent) => {
  e?.preventDefault();
  const target = document.getElementById('download') || document.getElementById('fc-get-app');
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const PublicHeader: React.FC<{ lang: SiteLang }> = ({ lang }) => {
  const copy = SITE_COPY[lang];
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showTop, setShowTop] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isLegal = /\/(privacy|terms|delete-account)$/.test(location.pathname);
  useReveal([location.pathname]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      setScrolled(y > 8);
      setShowTop(y > 600);
      if (progressRef.current) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progressRef.current.style.transform = `scaleX(${max > 0 ? Math.min(y / max, 1) : 0})`;
      }
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [navOpen]);

  const close = () => setNavOpen(false);

  return (
    <>
      <header className={`home-header ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="home-container header-inner">
          <Link to={copy.home} className="brand" onClick={close}>
            <img src={logo} alt="Fconnect" />
          </Link>
          <nav id="main-navigation" className={`main-nav ${navOpen ? 'open' : ''}`}>
            {copy.nav.map((item) => (
              <NavLink key={item.to} to={item.to} end onClick={close}>
                {item.label}
              </NavLink>
            ))}
            <Link to={copy.langSwitch.to} className="lang-link" onClick={close}>
              {copy.langSwitch.label}
            </Link>
          </nav>
          <div className="fc-header-actions">
            <a href="#download" className="fc-btn fc-btn--primary header-cta" onClick={scrollToDownload}>
              {copy.downloadCta}
            </a>
            <button
              className="nav-toggle"
              aria-label={navOpen ? copy.menuClose : copy.menuOpen}
              aria-expanded={navOpen}
              aria-controls="main-navigation"
              onClick={() => setNavOpen((o) => !o)}
            >
              <span className={`burger ${navOpen ? 'is-open' : ''}`} aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
        {isLegal && <div className="fc-progress" ref={progressRef} aria-hidden="true" />}
      </header>
      <div className={`nav-backdrop ${navOpen ? 'open' : ''}`} onClick={close} />
      <button
        type="button"
        className={`fc-to-top ${showTop ? 'is-shown' : ''}`}
        aria-label={copy.backToTop}
        tabIndex={showTop ? 0 : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      </button>
    </>
  );
};

export default PublicHeader;
